import { useEffect, useRef } from "react";

import { BUCKET, pruefe, sb } from "./daten";
import { firmaSicherstellen } from "./firma";
import { dateiLesen, zusammenfuehren, type ERechnung } from "./kern/eRechnung";
import type { Beleg } from "./kern/typen";

const MIME: Record<string, string> = {
  pdf: "application/pdf",
  xml: "application/xml",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
};
const endung = (name: string) => name.split(".").pop()?.toLowerCase() ?? "";
const istXml = (datei: File) => endung(datei.name) === "xml";

const ablegen = async (datei: File) => {
  // Zufälliger Name: Dateinamen können Sensibles enthalten und stünden sonst in Storage-Logs
  const pfad = `${crypto.randomUUID()}.${endung(datei.name) || "bin"}`;
  const { error } = await sb()
    .storage.from(BUCKET)
    .upload(pfad, datei, {
      contentType: MIME[endung(datei.name)] ?? datei.type,
    });
  if (error) throw new Error(error.message);
  return pfad;
};

type Gruppe = { dateien: File[]; auslesen: ERechnung[] };

// Dateien derselben Rechnung (gleiche Nummer, z. B. PDF und XML) werden ein Beleg.
// Eine einzelne PDF ohne Auslese gehört zur einzigen erkannten Rechnung, sonst bleibt sie für sich.
const gruppieren = async (dateien: File[]) => {
  const gelesen = await Promise.all(
    dateien.map(async (d) => ({ datei: d, auslese: await dateiLesen(d) })),
  );
  const gruppen = new Map<string, Gruppe>();
  const rest: File[] = [];
  for (const { datei, auslese } of gelesen) {
    if (!auslese) {
      rest.push(datei);
      continue;
    }
    const schluessel = auslese.nummer ?? datei.name;
    const g = gruppen.get(schluessel) ?? { dateien: [], auslesen: [] };
    g.dateien.push(datei);
    g.auslesen.push(auslese);
    gruppen.set(schluessel, g);
  }
  const ohneXml = rest.filter((d) => !istXml(d));
  if (gruppen.size === 1 && ohneXml.length === 1) {
    [...gruppen.values()][0].dateien.push(ohneXml[0]);
    return [
      ...gruppen.values(),
      ...rest
        .filter((d) => d !== ohneXml[0] && !istXml(d))
        .map((d) => ({ dateien: [d], auslesen: [] })),
    ];
  }
  // Fremde XML (keine Rechnung) werden nicht abgelegt
  return [
    ...gruppen.values(),
    ...ohneXml.map((d) => ({ dateien: [d], auslesen: [] })),
  ];
};

export const belegeAnlegen = async (
  dateien: File[],
  richtung: Beleg["richtung"],
  email_id: number | null = null,
) => {
  let angelegt = 0;
  for (const gruppe of await gruppieren(dateien)) {
    const haupt = gruppe.dateien.find((d) => !istXml(d)) ?? gruppe.dateien[0];
    const xml = gruppe.dateien.find((d) => istXml(d) && d !== haupt);
    const r = zusammenfuehren(gruppe.auslesen);
    const pfad = await ablegen(haupt);
    const pfad_xml = xml ? await ablegen(xml) : null;
    const gegenpartei = richtung === "ausgabe" ? r?.lieferant : r?.kunde;
    // Firma und Ansprechpartner im CRM anlegen oder ergänzen; ein Fehler dabei hält den Beleg nicht auf
    const firma_id = gegenpartei?.name
      ? await firmaSicherstellen(gegenpartei, `${richtung === "ausgabe" ? "Eingangsrechnung" : "Ausgangsrechnung"} ${r?.nummer ?? ""}`.trim()).catch(() => null)
      : null;
    pruefe(
      await sb()
        .from("bh_beleg")
        .insert({
          firma_id,
          richtung,
          pfad,
          pfad_xml,
          dateiname: haupt.name,
          mime: MIME[endung(haupt.name)] ?? haupt.type,
          email_id: angelegt === 0 ? email_id : null,
          datum: r?.datum ?? null,
          brutto:
            r?.brutto !== null && r?.brutto !== undefined
              ? Math.abs(r.brutto) / 100
              : null,
          brutto_ermaessigt: r?.bruttoErmaessigt
            ? r.bruttoErmaessigt / 100
            : null,
          partner: (richtung === "ausgabe" ? r?.verkaeufer : r?.kaeufer) ?? "",
          nummer: r?.nummer ?? null,
          satz: r?.satz ?? null,
          hinweis:
            [
              r
                ? `Aus ${r.format === "EFactuur" ? "Kurz-XML" : r.format === "CII" ? "ZUGFeRD/XRechnung (CII)" : "XRechnung/UBL"} gelesen`
                : null,
              ...(r?.hinweise ?? []),
              r?.gutschrift ? "Gutschrift: Betrag prüfen" : null,
            ]
              .filter(Boolean)
              .join(". ") || null,
        }),
    );
    angelegt++;
  }
  return angelegt;
};

// Rechnungen aus allen abgerufenen Postfächern: Anhang ist eine E-Rechnung oder Betreff/Dateiname nennt eine Rechnung
const RECHNUNG =
  /(rechnung|invoice|factuur|faktura|facture|receipt|quittung|beleg)/i;
type Anhang = { name: string; mime: string; path: string | null };
type Mail = { id: number; subject: string | null; attachments: Anhang[] };

const kandidat = (m: Mail) => {
  const anhaenge = (m.attachments ?? []).filter(
    (a) => a.path && /pdf|xml|image/.test(a.mime),
  );
  const xml = anhaenge.some(
    (a) => /xml/.test(a.mime) || a.name.toLowerCase().endsWith(".xml"),
  );
  const pdf = anhaenge.some((a) => /pdf/.test(a.mime));
  const genannt = RECHNUNG.test(
    `${m.subject ?? ""} ${anhaenge.map((a) => a.name).join(" ")}`,
  );
  return xml || (pdf && genannt) ? anhaenge : [];
};

export const postfachAbgleichen = async (bekannt: Set<number>) => {
  const { data } = pruefe(
    await sb()
      .from("emails")
      .select("id, subject, attachments")
      .eq("direction", "in")
      .neq("attachments", "[]")
      .order("id", { ascending: false })
      .limit(500),
  );
  let neu = 0;
  for (const mail of (data ?? []) as Mail[]) {
    if (bekannt.has(mail.id)) continue;
    const anhaenge = kandidat(mail);
    if (!anhaenge.length) continue;
    const dateien: File[] = [];
    for (const a of anhaenge) {
      const { data: blob } = await sb()
        .storage.from("attachments")
        .download(a.path as string);
      if (blob) dateien.push(new File([blob], a.name, { type: a.mime }));
    }
    const belegt = dateien.length
      ? await belegeAnlegen(dateien, "ausgabe", mail.id)
      : 0;
    // Mail ohne erkennbaren Beleg trotzdem merken, sonst wird sie bei jedem Öffnen erneut geladen
    if (!belegt) {
      pruefe(
        await sb()
          .from("bh_beleg")
          .insert({
            richtung: "ausgabe",
            pfad: `verworfen-${mail.id}`,
            dateiname: mail.subject ?? "",
            email_id: mail.id,
            verworfen: true,
          }),
      );
    }
    neu += belegt;
  }
  return neu;
};

// Einmal je Seitenaufruf abgleichen
export const usePostfachAbgleich = (
  belege: Beleg[],
  neuLaden: () => Promise<unknown>,
  melden: (n: number) => void,
) => {
  const gelaufen = useRef(false);
  useEffect(() => {
    if (gelaufen.current) return;
    gelaufen.current = true;
    const bekannt = new Set(
      belege.map((b) => b.email_id).filter((i): i is number => i !== null),
    );
    postfachAbgleichen(bekannt)
      .then(async (n) => {
        if (n) {
          await neuLaden();
          melden(n);
        }
      })
      .catch(() => {
        // Abgleich ist Komfort; Fehler zeigt die nächste Sitzung erneut
      });
  }, [belege, neuLaden, melden]);
};
